/**
 * GOOGLE CAST — the sender.
 *
 * ── The constraint, stated plainly, because it is the whole shape of this file
 *
 * You cannot Chromecast a web app from an iPhone. Apple does not expose the
 * Cast API in Safari, and every browser on iOS — Chrome, Firefox, Edge, all of
 * them — is Safari underneath. AirPlay screen mirroring exists, but the Web
 * AirPlay API only works on <video> and <audio> elements, not arbitrary DOM.
 *
 * There is no clever way around this. So we do not fake it, we do not promise
 * it, and we never render a Cast button that cannot cast. On iOS — and on
 * Firefox, and on desktop Safari — casting degrades to the QR path, which
 * works on every television ever made that has a browser, and which is the
 * path most families will use anyway.
 *
 * ── The second constraint: the App ID
 *
 * A Cast receiver is not a URL you hand to a Chromecast. It is an APPLICATION,
 * registered in the Google Cast Developer Console against a Google account, for
 * a one-time $5 fee. The generic Default/Styled Media Receivers cannot help us:
 * they play a media file, they do not load a web page.
 *
 * So the App ID is an environment variable (`VITE_CAST_APP_ID`), and when it is
 * absent — as it is until Arslan registers one — this module reports
 * `unconfigured` and the whole feature degrades to the QR path. It does not
 * throw, it does not warn the user, and it certainly does not show a button
 * that does nothing.
 *
 * See `.nookli/sprints/sprint-3/_design/cast-setup.md` for the exact steps.
 */

/** The receiver listens on this. Registered in the Cast console alongside the app. */
export const CAST_NAMESPACE = 'urn:x-cast:ai.nookli.wordrush';

const SENDER_SDK = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

export type CastAvailability =
  /** No Cast API on this platform and there never will be. iOS, Firefox, Safari. */
  | 'unsupported'
  /** Cast works here, but nobody has registered a receiver App ID yet. */
  | 'unconfigured'
  /** The API is up and no receiver is on the network — a picker would be empty. */
  | 'no-devices'
  /** Devices are there. The button opens the real picker. */
  | 'available'
  /** We are casting right now. */
  | 'connected';

export interface CastStatus {
  availability: CastAvailability;
  /** The device we're casting to, when we are. */
  deviceName: string | null;
  /** The room the receiver has been told to show. */
  roomCode: string | null;
}

type Listener = (status: CastStatus) => void;

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    chrome?: any;
    cast?: any;
    __onGCastApiAvailable?: (available: boolean) => void;
  }
}

/**
 * Is this an Apple mobile device — i.e. a browser that CANNOT cast, whatever
 * it calls itself?
 *
 * The iPad-on-desktop-UA case is the one that catches people out: iPadOS 13+
 * reports itself as a Mac. `maxTouchPoints > 1` on a "Mac" is an iPad.
 */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

/** The receiver App ID, or null if Arslan hasn't registered one yet. */
export function castAppId(): string | null {
  const id = (import.meta.env.VITE_CAST_APP_ID || '').trim();
  return id || null;
}

/**
 * Could the Cast SDK ever work in this browser?
 *
 * `window.chrome` exists in Chrome, Edge and Android Chrome — and, critically,
 * NOT in any iOS browser, nor in Firefox or desktop Safari. This is a check we
 * make BEFORE loading the SDK: there is no sense fetching 100kb of Google's
 * JavaScript into a browser that has no Cast machinery to hand it to.
 */
function castCouldExist(): boolean {
  if (typeof window === 'undefined') return false;
  if (isAppleMobile()) return false;
  return !!window.chrome;
}

class CastService {
  private status: CastStatus = { availability: 'unsupported', deviceName: null, roomCode: null };
  private listeners = new Set<Listener>();
  private loading: Promise<boolean> | null = null;
  private context: any = null;
  /** The room we are meant to be showing. Re-sent whenever a session begins. */
  private pendingRoom: string | null = null;

  getStatus(): CastStatus {
    return this.status;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }

  private set(next: Partial<CastStatus>) {
    this.status = { ...this.status, ...next };
    for (const fn of this.listeners) fn(this.status);
  }

  /**
   * Load the Cast sender SDK and wire up the context. Safe to call repeatedly;
   * safe to call on a platform that cannot cast (it resolves false and does
   * nothing else).
   */
  async init(): Promise<boolean> {
    if (this.loading) return this.loading;

    this.loading = (async () => {
      if (!castCouldExist()) {
        this.set({ availability: 'unsupported' });
        return false;
      }
      const appId = castAppId();
      if (!appId) {
        // TODO(cast-app-id): register a Custom Receiver in the Google Cast
        // Developer Console, point it at https://<host>/cast, and set
        // VITE_CAST_APP_ID. Until then this is honestly unconfigured, and the
        // UI shows the QR path instead of a button that cannot work.
        this.set({ availability: 'unconfigured' });
        return false;
      }

      try {
        await this.loadSdk();
      } catch {
        this.set({ availability: 'unsupported' });
        return false;
      }

      const cast = window.cast;
      const chrome = window.chrome;
      if (!cast?.framework || !chrome?.cast) {
        this.set({ availability: 'unsupported' });
        return false;
      }

      this.context = cast.framework.CastContext.getInstance();
      this.context.setOptions({
        receiverApplicationId: appId,
        // Leaving the session up when the tab closes is right for a television:
        // the family photo should stay on the wall after the host locks their
        // phone. Stopping it is an explicit act.
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        // Keep casting when the host navigates around the app — they will be
        // playing the game in this very tab.
        resumeSavedSession: true,
      });

      const CastContextEventType = cast.framework.CastContextEventType;
      this.context.addEventListener(CastContextEventType.CAST_STATE_CHANGED, (e: any) =>
        this.onCastState(e.castState),
      );
      this.context.addEventListener(CastContextEventType.SESSION_STATE_CHANGED, (e: any) =>
        this.onSessionState(e),
      );

      this.onCastState(this.context.getCastState());
      return true;
    })();

    return this.loading;
  }

  private loadSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.cast?.framework) return resolve();

      const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SENDER_SDK.split('?')[0]}"]`);
      const timeout = setTimeout(() => reject(new Error('cast sdk timeout')), 8000);

      window.__onGCastApiAvailable = (available: boolean) => {
        clearTimeout(timeout);
        if (available) resolve();
        else reject(new Error('cast api unavailable'));
      };

      if (existing) return; // its callback will fire

      const s = document.createElement('script');
      s.src = SENDER_SDK;
      s.async = true;
      s.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('cast sdk failed to load'));
      };
      document.head.appendChild(s);
    });
  }

  private onCastState(state: string) {
    const S = window.cast?.framework?.CastState;
    if (!S) return;
    if (state === S.NO_DEVICES_AVAILABLE) this.set({ availability: 'no-devices', deviceName: null });
    else if (state === S.NOT_CONNECTED) this.set({ availability: 'available', deviceName: null });
    else if (state === S.CONNECTING) this.set({ availability: 'available' });
    else if (state === S.CONNECTED) {
      const session = this.context?.getCurrentSession?.();
      this.set({
        availability: 'connected',
        deviceName: session?.getCastDevice?.()?.friendlyName || 'your TV',
      });
    }
  }

  private onSessionState(e: any) {
    const S = window.cast?.framework?.SessionState;
    if (!S) return;
    // A session that has just started — or one we RE-joined after a reload —
    // has a receiver on the far end that may know nothing about our room. Tell
    // it again. Re-sending a room code is idempotent; not sending it is a
    // television showing the wrong game.
    if (e.sessionState === S.SESSION_STARTED || e.sessionState === S.SESSION_RESUMED) {
      if (this.pendingRoom) this.sendRoom(this.pendingRoom);
    }
    if (e.sessionState === S.SESSION_ENDED) {
      this.set({ availability: 'available', deviceName: null, roomCode: null });
    }
  }

  private sendRoom(roomCode: string): boolean {
    const session = this.context?.getCurrentSession?.();
    if (!session) return false;
    try {
      session.sendMessage(CAST_NAMESPACE, { type: 'show-room', roomCode });
      this.set({ roomCode });
      return true;
    } catch (err) {
      console.warn('cast: sendMessage failed', err);
      return false;
    }
  }

  /**
   * Open the REAL device picker, and hand the receiver the room code once the
   * user has chosen a television.
   *
   * Resolves false when the user dismisses the picker — which is a perfectly
   * ordinary thing to do and is not an error.
   */
  async castRoom(roomCode: string): Promise<boolean> {
    const ready = await this.init();
    if (!ready || !this.context) return false;

    this.pendingRoom = roomCode.toUpperCase();

    try {
      // Already casting? Just retarget the television at this room.
      if (this.context.getCurrentSession()) {
        return this.sendRoom(this.pendingRoom);
      }
      await this.context.requestSession(); // ← the picker. A real one.
      return this.sendRoom(this.pendingRoom);
    } catch (err: any) {
      // 'cancel' is the user closing the picker. Say nothing.
      if (err === 'cancel' || err?.code === 'cancel') return false;
      console.warn('cast: requestSession failed', err);
      return false;
    }
  }

  stop(): void {
    try {
      this.context?.endCurrentSession?.(true);
    } catch { /* nothing to end */ }
    this.pendingRoom = null;
    this.set({ availability: 'available', deviceName: null, roomCode: null });
  }
}

export const castService = new CastService();
